import { isCellOfTarget } from '@/pages/cvc/util/matrixKey';
import {
  generateMatrixModelSelector,
  generateTargetListSelector,
} from '@/stores/cvc';
import { useCallback } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

type DIMENSION = 'row' | 'col';
const isAllChecked = (matrix: boolean[]) => matrix.every((item) => item);
const useMatrix = () => {
  const [matrix, setMatrix] = useRecoilState(generateMatrixModelSelector);

  const toggleCells = useCallback(
    (key: string, dimension: DIMENSION, isChecked?: boolean) => {
      const list = Object.keys(matrix).filter((item: string) =>
        dimension === 'row'
          ? item.startsWith(`${key}:`)
          : item.endsWith(`:${key}`)
      );
      const isAll =
        isChecked ?? !isAllChecked(list.map((item) => matrix[item]));
      setMatrix((pre) => {
        const newMatrix = { ...pre };
        list.forEach((item) => {
          newMatrix[item] = isAll;
        });
        return newMatrix;
      });
    },
    [setMatrix, matrix]
  );
  const generateTargetList = useRecoilValue(generateTargetListSelector);
  const toggleGroupCells = useCallback(
    (group: string, value: boolean) => {
      const groupItems = generateTargetList.filter(
        (target) => target.groupName === group
      );
      const list = Object.keys(matrix).filter((item: string) => {
        return groupItems.some((groupItem) =>
          isCellOfTarget(item, groupItem.id)
        );
      });
      setMatrix((pre) => {
        const newMatrix = { ...pre };
        list.forEach((item) => {
          newMatrix[item] = value;
        });
        return newMatrix;
      });
    },
    [generateTargetList, matrix, setMatrix]
  );
  // Answers the question the name asks, and the one the `has-checked`
  // indeterminate style asks: does this group have ANY checked cell? It used
  // to answer `.every`, so a group with one of three cells checked showed no
  // partial state, and a group whose filter matched no key reported partial
  // via `[].every() === true`. `.some` fixes both: unlike the `isChecked`
  // computations that pair `.every` with a `!!length` guard against exactly
  // that vacuous truth, `[].some()` is already false, so a length guard here
  // would be unreachable code implying `.some` needs help (ISSUE-061).
  const hasCheckedItem = useCallback(
    (group: string) => {
      const groupItems = generateTargetList.filter(
        (target) => target.groupName === group
      );
      return Object.keys(matrix)
        .filter((item: string) => {
          return groupItems.some((groupItem) =>
            isCellOfTarget(item, groupItem.id)
          );
        })
        .some((item) => matrix[item]);
    },
    [matrix, generateTargetList]
  );

  return {
    toggleCells,
    matrix,
    setMatrix,
    isAllChecked,
    toggleGroupCells,
    hasCheckedItem,
  };
};

export default useMatrix;
